import {
  TrpcAuthNotReadyError,
  TrpcTokenRefreshError,
} from '../utils/trpcUtils';

/**
 * The literal rather than `logoutUrl()` from `@/utils/urls`, matching
 * `ApolloGraphqlProvider.getAuthHeaders`. That module imports `Parent_Type_Enum`
 * from the generated GraphQL client, which would pull the whole generated schema
 * onto the auth path and make this module unloadable without codegen having run.
 * Keep in step with `logoutUrl()` if the route ever moves.
 */
const LOGOUT_PATH = '/logout';

interface TrpcAuthHeadersOptions {
  /** Auth0 `isLoading` — read at call time, not captured. */
  isLoading: () => boolean;
  /** Auth0 `isAuthenticated` — read at call time, not captured. */
  isAuthenticated: () => boolean;
  /** Auth0 `getAccessTokenSilently` — read at call time, not captured. */
  getToken: () => Promise<string>;
  /**
   * Drives re-authentication after a failed silent refresh. Defaults to the
   * real navigation; overridden in tests, which cannot navigate the runner.
   */
  redirectToLogout?: () => void;
}

/**
 * Builds the `headers()` callback for the tRPC links in `TrpcProvider`.
 *
 * Extracted from the provider so its branch selection is unit-testable without
 * React — the same reason `graphqlErrorHandler.ts` was lifted out of
 * `ApolloGraphqlProvider` (RSP-6971).
 *
 * **Throws rather than returning `{}` (COI-897).** Both branches used to return
 * an empty header object, so the request went out with no `Authorization`
 * header and `express-jwt` answered 401 — 159 of them in 48h, with no re-auth
 * prompt for the user and no way to tell the two causes apart. `@trpc/client`
 * awaits `headers()` *before* it calls `fetch`, so throwing aborts the request
 * outright instead of sending one that cannot succeed. Nothing legitimate is
 * lost: `expressjwt(...).unless({ path: ['/healthz', '/version'] })` guards
 * every `/trpc/*` route (packages/trpc/src/trpc-server.ts), and no router uses
 * `publicProcedure`.
 */
export const createTrpcAuthHeaders = ({
  isLoading,
  isAuthenticated,
  getToken,
  redirectToLogout = () => {
    window.location.href = LOGOUT_PATH;
  },
}: TrpcAuthHeadersOptions) => {
  // Per-client, so a burst of concurrent queries produces ONE navigation rather
  // than one each. Batching is off (`maxItems: 1`, COI-451), so one query is one
  // request: COI-882 measured 64 landing in a single second as a page fanned
  // out, all of which call headers(). Closure state rather than module state so
  // it needs no reset between tests.
  let hasRedirectedToLogout = false;

  return async (): Promise<Record<string, string>> => {
    // Auth0 has not settled yet, so no token can exist. Deliberately does NOT
    // redirect: the login flow itself runs in this state, and React Query's
    // default retries pick the request up again once auth resolves.
    if (isLoading() || !isAuthenticated()) {
      throw new TrpcAuthNotReadyError();
    }

    try {
      return { Authorization: `Bearer ${await getToken()}` };
    } catch (error) {
      // The session can no longer be renewed silently. Redirect to /logout —
      // the same re-auth mechanism the GraphQL path uses (RSP-4478,
      // ApolloGraphqlProvider.getAuthHeaders). Apollo and tRPC are mounted
      // together during the migration, so a second, different mechanism here
      // (the ticket suggested `loginWithRedirect`) would race Apollo's.
      if (!hasRedirectedToLogout) {
        hasRedirectedToLogout = true;
        redirectToLogout();
      }

      throw new TrpcTokenRefreshError({ cause: error });
    }
  };
};
